import { makeAutoObservable } from 'mobx';

import { DataSourceDataGridStore } from 'areas/data-sources/stores/dataSourceDataGridStore';
import { DataSourceDataConstructorProps } from 'areas/data-sources/components/DataSourceDataConstructor/types';
import { GridSelectionType } from 'controls/grid/gridConfiguration';
import { MobxManager } from 'framework/mobx-integration';
import { DataSourceDataConstructor } from 'areas/data-sources/components/DataSourceDataConstructor/index';

const i18n = require('core/localization/localization').translator({
	'categorySelection title': {
		en: 'Select a Category',
		no: 'Velg en kategori',
	},
	'categorySelection info': {
		en: '<b>Category Column</b>: This column defines the categories that will appear on your chart',
		no: '<b>Kategori kolonne</b>: Denne kolonnen definerer kategoriene som vil vises i diagrammet ditt.',
	},
	'categorySelection help': {
		en: '<b>Select a Category:</b></br>Begin by choosing how you want to group or categorize the data in your chart.</br></br><b>Category Column:</b></br> Select a column that contains string values (e.g., names, types, labels).  This column defines the categories that will appear on your chart.',
		no: '<b>Velg en kategor:</b></br>Begynn med å velge hvordan du vil gruppere eller kategorisere dataene i diagrammet ditt.</br></br><b>Kategori kolonne:</b></br> Velg en kolonne som inneholder tekstverdier (f.eks. navn, typer, etiketter). Denne kolonnen definerer kategoriene som vil vises i diagrammet ditt.',
	},

	'columnsSelection title': {
		en: 'Select Data Columns',
		no: 'Velg datakolonner',
	},
	'columnsSelection info': {
		en: '<b>Data Column</b>: These column will provide the data used in the chart',
		no: '<b>Disse kolonnene</b>: vil gi dataene som brukes i diagrammet.',
	},
	'columnsSelection help': {
		en: '<b>Select Data Columns:</b></br>Next, choose the actual data you want to visualize.</br></br><b>Data Columns:</b></br>Pick one or more columns that contain numeric values (integer or double). These will provide the data points used in the chart.',
		no: '<b>Velg datakolonner:</b></br>Velg deretter hvilke data du ønsker å visualisere.</br></br><b>Datakolonner:</b></br>Velg én eller flere kolonner som inneholder numeriske verdier (heltall eller desimaltall). Disse vil gi datapunktene som brukes i diagrammet.',
	},

	'rowsSelection title': {
		en: 'Filter by Data Rows',
		no: 'Filtrer etter datarader',
	},
	'rowsSelection info': {
		en: '<b>Data Row</b>: Select the rows you want to display in the chart',
		no: '<b>Velg radene</b>: du ønsker å vise i diagrammet.',
	},
	'rowsSelection help': {
		en: '<b>Filter by Data Rows:</b></br>Finally, decide which specific data entries should be included.</br></br><b>Data Rows:</b></br>Select the rows from your datasource that you want to display in the chart. This lets you control which data points are visualized.',
		no: '<b>Filtrer etter datarader:</b></br>Til slutt, bestem hvilke rader som skal inkluderes.</br></br><b>Datarader:</b></br>Velg radene fra datakilden din som du vil vise i diagrammet. Dette lar deg kontrollere hvilke datapunkter som visualiseres.',
	},
	'Create datasource chart': {
		no: 'Opprett datakildediagram',
	},
});

export class DataSourceDataConstructorStore {
	dataSourceDataGridStore: DataSourceDataGridStore;

	categoryColumn: string;
	dataColumns: string[] = [];

	step: 'categorySelection' | 'columnsSelection' | 'rowsSelection' = 'categorySelection';

	mobx = new MobxManager();

	constructor(public props: DataSourceDataConstructorProps) {
		makeAutoObservable(this);
	}

	get gridStore() {
		return this.dataSourceDataGridStore?.gridStore;
	}

	get title() {
		return i18n(this.step + ' title');
	}

	get infoMessage() {
		return i18n(this.step + ' info');
	}

	async init() {
		this.dataSourceDataGridStore = new DataSourceDataGridStore();
		this.dataSourceDataGridStore.props = {
			dataSourceId: this.props.dataSourceId,
			accountId: this.props.accountId,
		};

		const config = await this.dataSourceDataGridStore.getDefaultGridConfig();
		config.columnsSelector = {
			selection: GridSelectionType.One,
		};

		this.dataSourceDataGridStore.initStore(config);

		this.mobx.when(
			() => this.dataSourceDataGridStore.gridStore.dataProvider.initialized,
			() => {
				this.switchToCategorySelectionStep();
			},
		);
	}

	onNextClick = async () => {
		if (this.step == 'categorySelection') {
			this.categoryColumn = this.gridStore.columns.selectedColumns[0];
			this.switchToColumnSelectionStep();
		} else if (this.step == 'columnsSelection') {
			this.dataColumns = [...this.gridStore.columns.selectedColumns];

			this.props.onCompleted({
				dataSourceId: this.props.dataSourceId,
				accountId: this.props.accountId,
				categoryColumn: this.categoryColumn,
				dataColumns: this.dataColumns,
				rows: null,
			});

			//this.switchToRowSelectionStep()
		}

		// else if(this.step == 'rowsSelection'){
		// 	this.props.onCompleted({
		// 		dataSourceId: this.props.dataSourceId,
		// 		accountId: this.props.accountId,
		// 		categoryColumn: this.categoryColumn,
		// 		dataColumns: this.dataColumns,
		// 		rows: this.gridStore.selection.dataset.items.map(item => item.data[this.categoryColumn])
		// 	})
		// }
	};

	onBackClick = () => {
		if (this.step == 'columnsSelection') {
			this.switchToCategorySelectionStep();
		} else if (this.step == 'rowsSelection') {
			this.switchToColumnSelectionStep();
		}
	};

	switchToCategorySelectionStep = () => {
		this.step = 'categorySelection';

		if (typeof this.gridStore.config.columns != 'function') {
			this.gridStore.config.columns.forEach((c) => {
				const columnFilter = this.gridStore.filtersConfiguration[c.field];
				c.selectable = columnFilter?.type == 'text';
			});
			if (this.categoryColumn) {
				const categoryColumn = this.gridStore.config.columns.find((c) => c.field == this.categoryColumn);
				categoryColumn.selectable = true;
				this.gridStore.columns.selectedColumns = [this.categoryColumn];
			}
			this.gridStore.config.columnsSelector.selection = GridSelectionType.One;
		}
	};

	switchToColumnSelectionStep = () => {
		this.step = 'columnsSelection';

		if (typeof this.gridStore.config.columns != 'function') {
			this.gridStore.config.columns.forEach((c) => {
				const columnFilter = this.gridStore.filtersConfiguration[c.field];
				c.selectable = columnFilter?.type == 'text' || columnFilter.type == 'integer';
			});

			const categoryColumn = this.gridStore.config.columns.find((c) => c.field == this.categoryColumn);
			categoryColumn.selectable = false;

			this.gridStore.columns.selectedColumns = [...this.dataColumns];
			this.gridStore.config.columnsSelector.selection = GridSelectionType.Many;
		}
	};

	switchToRowSelectionStep = () => {
		this.step = 'rowsSelection';

		if (typeof this.gridStore.config.columns != 'function') {
			this.gridStore.config.columnsSelector.selection = GridSelectionType.None;
		}
	};

	get nextEnabled() {
		if (this.step == 'categorySelection' || this.step == 'columnsSelection') {
			return this.gridStore?.columns.selectedColumns.length > 0;
		} else {
			return this.gridStore.selection.isAnythingSelected;
		}
	}

	get helpHeader() {
		return i18n('Create datasource chart');
	}

	get helpMessage() {
		return i18n(this.step + ' help');
	}

	destroy() {
		this.dataSourceDataGridStore?.destroy();
		this.mobx.destroy();
	}
}
